import _ from 'lodash';
import { momentProvider } from '@/services/clock.service';
import { StateService } from '@/services/state.service';

export const RulesServiceStates = {
	// Facilities page
	RESOURCE_LOADED: 'RESOURCE_LOADED',

	// Bookability page (tennis/pt/swim onboarding)
	BOOKABILITY_LOADED: 'BOOKABILITY_LOADED',

	// Pending Reservation page
	PENDING_CREATE: 'PENDING_CREATE',
	PENDING_LOADED: 'PENDING_LOADED',
	PENDING_FINISH: 'PENDING_FINISH',

	// Details page
	DETAILS_PAGE: 'DETAILS_PAGE',

	// ANY
	ANY: 'ANY'
};

export const RulesServiceTypes = {
	REGISTRATION: 'REGISTRATION', // New registration
	MODIFICATION: 'MODIFICATION', // Modify existing registration
	CANCELLATION: 'CANCELLATION'  // Cancel existing registration
};

export const RuleNames = {
	// Client-side rules defined only in client
	authenticated: 'authenticated',
	documentAcceptance: 'documentAcceptanceRule',
	participantSelection: 'participantSelection',

	// Client-side rules from Reg Api
	adultMinor: 'adultMinorRule',
	tooLate: 'tooLateRule',
	outOfMembershipLevel: 'outOfMembershipLevelRule',
	minimumDuration: 'minimumDurationRule',
	maxIntervals: 'maxIntervalsRule',
	notRegistrable: 'notRegistrableRule',
	rosterLimit: 'rosterLimitRule',
	tooSoon: 'tooSoonRule',
	maxReservationPerDay: 'maxReservationPerDayRule',
	notEditable: 'notEditableRule',
	tooLateEdit: 'tooLateEditRule',
	notCancellable: 'notCancellableRule',
	tooLateCancel: 'tooLateCancelRule',
	noCancelPaidEvent: 'noCancelPaidEvent',
	onlineCancellationCheck: 'onlineCancellationCheck',
	ageLimit: 'ageLimitRule',
	spotSelection: 'spotSelectionRule',

	// Server-side error codes
	nonAccess: 'nonAccess',
	advanceDays: 'advanceDays',
	resourceLevel: 'resourceLevel',
	memberLevel: 'memberLevel',
	noRules: 'noRules',
	pendingReservation: 'pendingReservation',
	costRestriction: 'costRestriction',
	callToReserve: 'callToReserve',
	dayPassCourts: 'dayPassCourts',
	unknown: 'unknown'
};

// TODO: Some things will need to be revisited:
// - Time zones
// - Boss / Exerp differences
// - This first pass was to get it implemented on the Pending Reservation page, so it may need rework/refactor for other situations

const rulesServiceConfigData = [
	// All Types
	{
		// Check to see if the user is authenticated.
		name: RuleNames.authenticated,
		validate: (params) => {
			return params.profile.isAuthenticated ? successResponse() : errorResponse();
		},
		states: [
			RulesServiceStates.RESOURCE_LOADED,
			RulesServiceStates.PENDING_CREATE,
			RulesServiceStates.PENDING_LOADED,
			RulesServiceStates.BOOKABILITY_LOADED
		],
		types: [
			RulesServiceTypes.REGISTRATION,
			RulesServiceTypes.MODIFICATION,
			RulesServiceTypes.CANCELLATION
		],
		error: () => 'User not authenticated.'
	},

	// REGISTRATION rules - for events, resources, and onboarding
	{
		name: RuleNames.tooLate,
		validate: (params) => {
			if (StateService.isMockData()) return successResponse();

			return momentProvider().add(params.rule.minutesFromStart, 'minutes').isAfter(params.reg.startMoment)
				? errorResponse()
				: successResponse();
		},
		states: [
			RulesServiceStates.PENDING_FINISH,
			RulesServiceStates.RESOURCE_LOADED,
			RulesServiceStates.BOOKABILITY_LOADED
		],
		types: [
			RulesServiceTypes.REGISTRATION
		],
		error: () => 'The registration booking window is closed.'
	},

	// REGISTRATION rules - for the pending reservation page when FINISH is clicked
	{
		// Only run this rule when completing the registration.
		name: RuleNames.adultMinor,
		validate: (params) => {
			if (!params.reg.selectedParticipants || params.reg.selectedParticipants.length < params.rule.minAttendees) return errorResponse();

			let adultOk = false;
			let minorOk = false;

			// we don't have a way to calculate exact age on the client-side, so we'll give the user the benefit of the doubt
			// the adultPerson *might* not be an adult now but will be on the date of the event.  Server-side validation will catch that.
			// the minorPerson *might* not be a minor on the date of the event.  Server-side validation will catch that.
			for (const person of params.reg.selectedParticipants) {
				if (person.age + 1 >= 18) adultOk = true;
				if (person.age < 18) minorOk = true;
			}

			return adultOk && minorOk ? successResponse() : errorResponse();
		},
		// specialRequirement: '*At least one adult and one junior needs to be selected in order to continue with registration.',
		// participantsMinimum: 2,
		states: [
			RulesServiceStates.PENDING_FINISH,
			RulesServiceStates.DETAILS_PAGE
		],
		types: [
			RulesServiceTypes.REGISTRATION
		]
	},
	{
		// Only run this rule when completing the registration.
		// This rule will not be exported by the RegApi rule aggregation
		name: RuleNames.documentAcceptance,
		validate: (params) => {
			if (!params.reg.waiverRequired) return successResponse();
			return params.reg.waiverAccepted ? successResponse(): errorResponse();
		},
		states: [
			RulesServiceStates.PENDING_FINISH
		],
		types: [
			RulesServiceTypes.REGISTRATION
		]
	},
	{
		// Only run this rule when completing the registration.
		// This rule will not be exported by the RegApi rule aggregation
		name: RuleNames.participantSelection,
		validate: (params) => {
			return params.reg.selectedParticipants.length > 0 ? successResponse(): errorResponse();
		},
		states: [
			RulesServiceStates.PENDING_FINISH
		],
		types: [
			RulesServiceTypes.REGISTRATION
		],
		error: () => 'You must select at least one participant.'
	},
	{
		// This rule only applies to resource reservations, but only needs to be run prior to finishing the reservation
		name: RuleNames.spotSelection,
		validate: (params) => {
			return params.reg.spot || !params.reg.hasSpots || params.reg.openSpots === 0
				? successResponse()
				: errorResponse();
		},
		states: [
			RulesServiceStates.PENDING_FINISH
		],
		types: [
			RulesServiceTypes.REGISTRATION
		]
	},
	{
		// This rule only applies to resource reservations, but only needs to be run prior to finishing the reservation
		name: RuleNames.minimumDuration,
		validate: (params) => {
			return params.reg.duration < params.rule.minDuration
				? errorResponse()
				: successResponse();
		},
		states: [
			RulesServiceStates.PENDING_FINISH
		],
		types: [
			RulesServiceTypes.REGISTRATION
		]
	},
	{
		// This rule only applies to resource reservations, but only needs to be run prior to finishing the reservation
		name: RuleNames.maxIntervals,
		validate: (params) => {
			return params.reg.duration > params.rule.maxDuration
				? errorResponse()
				: successResponse();
		},
		states: [
			RulesServiceStates.PENDING_FINISH
		],
		types: [
			RulesServiceTypes.REGISTRATION
		]
	},

	// REGISTRATION rules - when the bookability page is loaded (tennis/pt/swim onboarding)
	{
		// This rule is used by onboarding reg types.  Should probably be used in the bookability component
		name: RuleNames.rosterLimit,
		validate: (params) => {
			return params.reg.takenSpots && params.reg.totalSpots && params.reg.takenSpots > params.reg.totalSpots
				? errorResponse()
				: successResponse();
		},
		states: [
			RulesServiceStates.BOOKABILITY_LOADED
		],
		types: [
			RulesServiceTypes.REGISTRATION
		]
	},

	// REGISTRATION rules - for each time slot on the facilities page
	{
		// This rule only needs to be run on the facilities page
		name: RuleNames.tooSoon,
		validate: (params) => {
			// if the tooSoonRule was exported without a resourceLevel object, that is ok. Only reg types that are resource reservations will need this rule (for now)
			if (!params.rule.resourceLevel) return successResponse();

			return momentProvider().add(params.rule.minutesFromStart, 'minutes').isAfter(params.reg.startMoment)
				? errorResponse()
				: successResponse();
		},
		states: [
			RulesServiceStates.RESOURCE_LOADED
		],
		types: [
			RulesServiceTypes.REGISTRATION
		]
	},
	{
		// This rule only needs to be run on the facilities page
		name: RuleNames.maxReservationPerDay,
		validate: (params) => {
			// TODO: Implement
			Helpers.log('maxReservationsPerDay: ', params.rule.maxReservationsPerDay);
			return successResponse();
		},
		states: [
			RulesServiceStates.RESOURCE_LOADED
		],
		types: [
			RulesServiceTypes.REGISTRATION
		]
	},

	// MODIFICATION rules
	{
		name: RuleNames.notEditable,
		validate: (params) => {
			// TODO: Implement
			Helpers.log('modifySource: ', params.rule.modifySource);
			return successResponse();
		},
		states: [],
		types: [
			RulesServiceTypes.MODIFICATION
		]
	},
	{
		name: RuleNames.tooLateEdit,
		validate: (params) => {
			return momentProvider().add(params.rule.minutesFromStart, 'minutes').isAfter(params.reg.startMoment)
				? errorResponse()
				: successResponse();
		},
		states: [],
		types: [
			RulesServiceTypes.MODIFICATION
		]
	},

	// CANCELLATION rules
	{
		name: RuleNames.notCancellable,
		validate: () => {
			// TODO: Implement
			return successResponse();
		},
		states: [],
		types: [
			RulesServiceTypes.CANCELLATION
		]
	},
	{
		name: RuleNames.tooLateCancel,
		validate: (params) => {
			return momentProvider().add(params.rule.minutesFromStart, 'minutes').isAfter(params.reg.startMoment)
				? errorResponse()
				: successResponse();
		},
		states: [],
		types: [
			RulesServiceTypes.CANCELLATION
		]
	},
	{
		name: RuleNames.noCancelPaidEvent,
		validate: () => {
			// TODO: Implement
			return successResponse();
		},
		states: [],
		types: [
			RulesServiceTypes.CANCELLATION
		]
	},
	{
		name: RuleNames.onlineCancellationCheck,
		validate: () => {
			// TODO: Implement
			return successResponse();
		},
		states: [],
		types: [
			RulesServiceTypes.CANCELLATION
		]
	}
];

export const RulesServiceConfig = {
	getRule(ruleName, ruleType, ruleState) {
		const rule = _.find(rulesServiceConfigData, function(obj) {
			return obj.name === ruleName;
		});

		// Could not find the rule
		if (!rule) return null;

		// Could not find the rule or the rule is not in scope for the specified ruleType
		if (ruleType && !rule.types.includes(ruleType)) return null;

		// If ANY is passed in as the rule state, we will always return the rule
		if (ruleState === RulesServiceStates.ANY) return rule;

		// If the rule's defined states includes ANY, we will always return the rule
		if (rule.states.includes(RulesServiceStates.ANY)) return rule;

		// Skip if the rule is not in scope for the specified ruleState
		if (!rule.states.includes(ruleState)) return null;

		return rule;
	},

	getRuleNameFromServerError(reason) {
		// JM: copied over almost identically from angular, along with the comments
		// Frontend error codes not accounted for:
		//  - resourceLevel (shouldn't need)
		//  - memberLevel (shouldn't need)
		//  - noRules (shouldn't need)
		//  - pendingReservation
		//  - costRestriction (shouldn't need)
		//  - dayPassCourts (shouldn't need)
		switch (reason) {
			case 'NotAuthenticated':
				return RuleNames.authenticated;
			case 'OutOfMembershipLevel':
				return RuleNames.outOfMembershipLevel;
			case 'TooSoon':
				return RuleNames.advanceDays;
			case 'TooLate':
				return RuleNames.tooLate;
			case 'RosterLimit':
				return RuleNames.rosterLimit;
			case 'AlreadyRegistered':
				// (Carried over this way from angular) should this do anything on the frontend?
				return '';
			case 'DayPassUser':
				return RuleNames.nonAccess;
			case 'OnlineRegistrationNotAvailable':
				return RuleNames.callToReserve;
			case 'AdultMinor': {
				return RuleNames.adultMinor;
			}
			case 'NotRegistrable':
			case 'MemberError':
			case 'RegistrationNotFound':
			case 'EventNotFound':
				return RuleNames.notRegistrable;
			case 'AgeLimit':
				return RuleNames.ageLimit;
			case 'unknown':
				return RuleNames.unknown;
			default:
				return null;
		}
	},

	getNotificationErrors() {
		// These error conditions will always display their error messages in the Notification
		return [
			// Server errors
			RuleNames.outOfMembershipLevel,
			RuleNames.advanceDays,
			RuleNames.tooLate,
			RuleNames.rosterLimit,
			RuleNames.nonAccess,
			RuleNames.callToReserve,
			RuleNames.notRegistrable
		];
	}
};

function successResponse() {
	return {
		success: true,
		errorParams: null
	};
}

function errorResponse(errorParams) {
	return {
		success: false,
		errorParams: errorParams || {}
	};
}
